'use client'

/**
 * CreditBalanceCard v3 — Bill prototype 2026-05-30 (app.go-atrium.com .bcard).
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │ Credit balance                                                       │
 *   │ Top up directly or set up auto-reload to keep campaigns running.    │
 *   │ ┌─ cream subcard ─────────────────────────────────────────────────┐ │
 *   │ │ $24,300                          [Payment methods] [Buy credits] │ │
 *   │ │ Remaining balance · Auto-reload on at $5,000                     │ │
 *   │ └──────────────────────────────────────────────────────────────────┘ │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * The auto-reload inline link opens AutoReloadDialog; Buy credits opens
 * BuyCreditsDialog; the Payment methods button kicks off Stripe Checkout
 * via /api/wallet/payment-method. When the customer has a card on file we
 * surface the brand + last4 inline on the btn-out (live feature the prototype
 * doesn't show); otherwise a dashed "Add payment method" variant tails the
 * button row.
 */

import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { CreditCardIcon } from '@hugeicons-pro/core-stroke-rounded'
import { Button } from '@/components/ui/button'
import { AutoReloadDialog } from './AutoReloadDialog'
import { BuyCreditsDialog } from './BuyCreditsDialog'

interface PaymentMethod {
  id: string
  brand: string
  last4: string
}

interface AutoReloadState {
  enabled: boolean
  threshold_cents: number
  amount_cents: number
  has_payment_method: boolean
  payment_method: PaymentMethod | null
}

interface CreditBalanceCardProps {
  balanceCents: number | null
  onReload: () => void | Promise<void>
  walletLabel?: string
  walletSublabel?: string
}

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

function formatBrand(brand: string): string {
  const lower = brand.toLowerCase()
  if (lower === 'amex') return 'Amex'
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
}

export function CreditBalanceCard({
  balanceCents,
  onReload,
  walletLabel,
  walletSublabel,
}: CreditBalanceCardProps) {
  const [autoReload, setAutoReload] = useState<AutoReloadState | null>(null)
  const [autoReloadDialogOpen, setAutoReloadDialogOpen] = useState(false)
  const [buyDialogOpen, setBuyDialogOpen] = useState(false)
  const [pmRedirecting, setPmRedirecting] = useState(false)

  const loadAutoReload = useCallback(async () => {
    try {
      const res = await fetch('/api/wallet/auto-fund')
      if (!res.ok) return
      const data = (await res.json()) as AutoReloadState
      setAutoReload(data)
    } catch {
      // non-fatal
    }
  }, [])

  useEffect(() => {
    void loadAutoReload()
  }, [loadAutoReload])

  const handleUpdatePaymentMethod = useCallback(async () => {
    setPmRedirecting(true)
    try {
      const res = await fetch('/api/wallet/payment-method', { method: 'POST' })
      const data = (await res.json()) as { checkout_url?: string; error?: string }
      if (!res.ok || !data.checkout_url) {
        throw new Error(data.error || 'Failed to start checkout')
      }
      window.location.href = data.checkout_url
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start checkout')
      setPmRedirecting(false)
    }
  }, [])

  const isLoading = balanceCents === null || autoReload === null
  const pm = autoReload?.payment_method ?? null

  const autoReloadLabel = autoReload?.enabled
    ? `Auto-reload on at ${formatCurrency(autoReload.threshold_cents)}`
    : 'Set up auto-reload'

  return (
    <>
      <div className="bv3-bcard">
        <h3>{walletLabel ?? 'Credit balance'}</h3>
        <div className="h3sub">
          {walletSublabel ?? 'Top up directly or set up auto-reload to keep campaigns running.'}
        </div>

        <div className="bv3-subcard bv3-row-flex">
          {/* Left: balance + auto-reload inline link */}
          <div>
            {isLoading ? (
              <>
                <div className="h-9 w-32 rounded bg-[#E8E5DC] animate-pulse" />
                <div className="h-3 w-48 rounded bg-[#E8E5DC]/60 animate-pulse mt-2" />
              </>
            ) : (
              <>
                <div className="bv3-bigmono tabular-nums" style={{ fontSize: 34 }}>
                  {formatCurrency(balanceCents!)}
                </div>
                <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                  Remaining balance ·{' '}
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setAutoReloadDialogOpen(true)}
                    className="h-auto p-0 align-baseline font-semibold"
                    style={{ color: 'var(--color-brand)' }}
                  >
                    {autoReloadLabel}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Right: Payment methods + Buy credits */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {isLoading ? (
              <div className="h-10 w-40 rounded-[11px] bg-[#E8E5DC]/60 animate-pulse" />
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleUpdatePaymentMethod}
                disabled={pmRedirecting}
                className="tabular-nums"
                title={pm ? 'Update payment method' : 'Add payment method'}
              >
                <HugeiconsIcon
                  icon={CreditCardIcon}
                  className="size-4"
                  style={{ color: '#888' }}
                />
                {pm ? (
                  <>
                    <span>{formatBrand(pm.brand)}</span>
                    <span style={{ color: '#888' }}>•••• {pm.last4}</span>
                  </>
                ) : (
                  <span>Payment methods</span>
                )}
              </Button>
            )}

            <Button
              type="button"
              onClick={() => setBuyDialogOpen(true)}
              disabled={isLoading}
            >
              Buy credits
            </Button>
          </div>
        </div>
      </div>

      {autoReload && (
        <AutoReloadDialog
          open={autoReloadDialogOpen}
          onOpenChange={setAutoReloadDialogOpen}
          initial={autoReload}
          onChanged={async () => {
            await loadAutoReload()
            await onReload()
          }}
        />
      )}
      <BuyCreditsDialog
        open={buyDialogOpen}
        onOpenChange={setBuyDialogOpen}
        autoReload={autoReload}
        onPurchased={async () => {
          await onReload()
        }}
      />
    </>
  )
}
